import {
  Header,
  SearchFilterWrapper,
  setDateFromStorage,
  KPI_COLORS,
  zonedToUTC,
  zonedToUTCAdd1Day,
  useTranslation
} from '@fareye/common'
import {
  GraphRenderer,
  IApiChartConfig,
  IGraphRendererConfig
} from '@fareye/kpi'
import { IDateRange, IRouteConfig } from '@fareye/types'
import {
  DatePicker,
  Flex,
  RenderControl,
  SearchSelect,
  usePrevious
} from '@fareye/ui'
import {
  cancelToken,
  cloneDeep,
  currenthhmmA,
  get,
  getQueryParams,
  groupBy,
  isEmpty,
  isEqual,
  post,
  yyyyMmDd
} from '@fareye/utils'
import queryString, { ParsedQuery } from 'query-string'
import React from 'react'
import { RouteComponentProps } from 'react-router'
import ScoreCard from '../../components/ScoreCard'
import { GraphGrid, HorizontalScrollGrid } from '../../styled/styled'
import { GRAPH_DATA_MAPPING, SHIPMENT_TYPES } from '../../utility/constants'
//import KPI_CONFIG from '../OceanStatsKpi.json'
import KPI_CONFIG from '../../utility/parcelKpi.json'
import FullScreenComponent, { pageId } from './GraphFullScreen'
import ScoreCardListing from './ScoreCardListing'
interface IKpi extends RouteComponentProps {
  routesConfig?: Partial<IRouteConfig>[]
  config: any
}

const ParcelDashboard = ({
  match,
  location,
  history,
  routesConfig,
  config: dashboardConfig
}: IKpi) => {
  const searchParams: ParsedQuery<string | Date> = queryString.parse(
    location.search
  )
  const { filters, config } = dashboardConfig
  const [dateRange, setDateRange] = React.useState(() => {
    return setDateFromStorage({
      showDatePicker: true,
      carryDate: true,
      searchParams,
      daysDiff: 10
    })
  })
  const { t } = useTranslation()
  let pageHeaderType: any = dashboardConfig?.pageType
    ? dashboardConfig?.pageType
    : SHIPMENT_TYPES
  pageHeaderType = pageHeaderType.map((type: { label: any }) => ({
    ...type,
    label: t(type.label)
  }))
  const [selectedShipmentType, setSelectedShipmentType] = React.useState(
    pageHeaderType[0]
  )
  const [selectedKey, setSelectedKey] = React.useState('all')
  const source = cancelToken().source()
  const [lastUpdate, setLastUpdate] = React.useState<string>('')
  const [loading, setLoading] = React.useState(false)
  //baseURL = 'http://localhost:3001'
  const [selectedFilter, setSelectedFilter] = React.useState<any>([])
  const dataConfig = {
    getDataForChart: getDataForChartFromServer,
    graphDataMapping: GRAPH_DATA_MAPPING
  }

  const { toggleContent } = config
  const previousDateRange = usePrevious(dateRange)
  const [graphRenderListing, setGraphRenderListing] = React.useState<
    IGraphRendererConfig[]
  >([])
  const [scoreCardRenderingList, setScoreCardRenderingList] = React.useState<
    IGraphRendererConfig[]
  >([])
  const apiFav = filters.apiFav

  const apiFilter = React.useMemo(() => {
    let filterConfig = {
      ...filters.apiFilter,
      queryParams: { data_source: selectedShipmentType.dataSource }
    }
    return filterConfig
  }, [selectedShipmentType])

  React.useEffect(() => {
    setLoading(true)
    fetchConfig()
  }, [selectedShipmentType])

  React.useEffect(() => {
    if (!isEmpty(graphRenderListing)) {
      const clonedGraphRenderListing = cloneDeep(graphRenderListing)
      clonedGraphRenderListing.forEach(
        ele =>
          (ele.globalFilter = {
            dateRange: dateRange,
            selectedFilter: selectedFilter,
            selectedKey
          })
      )
      setGraphRenderListing(clonedGraphRenderListing)
      const clonedScoreCardListing = cloneDeep(scoreCardRenderingList)
      clonedScoreCardListing.forEach(
        ele =>
          (ele.globalFilter = {
            dateRange: dateRange,
            selectedFilter: selectedFilter,
            selectedKey
          })
      )
      setScoreCardRenderingList(clonedScoreCardListing)

      if (!isEqual(previousDateRange, dateRange)) {
        const changedQueryParam = getQueryParams({
          ...dateRange
        })
        history.replace({
          pathname: match.url,
          search: changedQueryParam
        })
      }
    }
  }, [dateRange, selectedFilter, selectedKey])
  const globalFilter = React.useMemo(() => {
    return {
      dateRange: dateRange,
      selectedFilter: selectedFilter,
      selectedKey,
      dataSource: selectedShipmentType
    }
  }, [dateRange, selectedFilter, selectedKey, selectedShipmentType])
  async function fetchConfig() {
    let { graphData } = config
    try {
      const queryParam = getQueryParams(graphData.queryParams)
      const jsonUrl = `${graphData.apiUrl}?${queryParam}`
      //const jsonUrl = `http://localhost:3001/data`
      const { data } = await get(jsonUrl, source.token)
      //const kpiRenderingConfig = KPI_CONFIG
      const kpiRenderingConfig = isEmpty(data.configs)
        ? data.samples
        : data.configs
      let kpiData = (kpiRenderingConfig as any[])
        .sort((next: any, pre: any) => next.data.order - pre.data.order)
        .map((ele: any) => ele.data)
      kpiData.map((ele: IGraphRendererConfig) => {
        ele.dataConfig = dataConfig
        ele.graphConfig.colors = { ...ele.graphConfig.colors, ...KPI_COLORS }
        // Keep explicit dataSources (e.g. AlertEvent). Only override Journey/Shipment
        // KPIs with the global shipment-type toggle.
        const configuredValue = ele.graphConfig?.dataSource?.value
        const isShipmentBound =
          !configuredValue || ['Journey', 'Shipment'].includes(configuredValue)
        if (isShipmentBound) {
          ele.graphConfig.dataSource = selectedShipmentType
        }
        ele.globalFilter = {
          dateRange: dateRange,
          selectedFilter: selectedFilter
        }
        return false
      })
      let scoreCardList = kpiData.filter(
        ele => ele.graphConfig.chartType === 'scorecard'
      )
      kpiData = kpiData.filter(ele => ele.graphConfig.chartType !== 'scorecard')
      setScoreCardRenderingList(scoreCardList)
      setGraphRenderListing(kpiData)
      setLoading(false)
    } catch (ex:any) {
      setLoading(false)
    }
  }
  async function getDataForChartFromServer(
    value: Partial<IApiChartConfig>,
    globalFilter: any,
    isReport?: boolean
  ) {
    const { graphDataMapping } = dataConfig
    const {
      dataSource,
      metric,
      metrics,
      hideDimension,
      metricUnit,
      dimension,
      breakdownDimension: breakdownDimensionFromKpi,
      benchmarkDimension: benchmarkDimensionFromKpi,
      dimensionalFilters,
      dimensionalAggs,
      filter: filterFromKpi,
      sortKey,
      sortType,
      size
    } = value
    const { dateRange, selectedFilter, selectedKey }: any = globalFilter || {}
    let breakDownDimension, benchmarkDimension, sort, metricCustom
    if (breakdownDimensionFromKpi) {
      breakDownDimension = {
        label: breakdownDimensionFromKpi['value']
      }
      if (breakdownDimensionFromKpi['filteration_params']) {
        ;(breakDownDimension as any)['filteration_params'] =
          breakdownDimensionFromKpi['filteration_params']
      }
    }
    metricCustom = {
      key: metric.aggKey['value'],
      type: metric.aggType['value'],
      unit: metricUnit ? metricUnit['value'] : ''
    }
    if (benchmarkDimensionFromKpi) {
      benchmarkDimension = {
        ...metricCustom,
        value: metric.value,
        breakDownDimension: metric.breakDownDimension
      }
    }

    sort = {
      ...(sortType ? { order: sortType['value'] } : {}),
      sortable: sortKey ? 'metric' : 'dimension',
      size: size || 10
    }
    let filter = filterFromKpi
      ? filterFromKpi.map(filterList => {
          return filterList.map(ele => {
            return {
              filterKey: ele.filterKey['value'],
              filterValue: ele.filterValue['value'],
              condition: ele.condition['value'],
              filterType: ele.filterType['value']
            }
          })
        })
      : []
    if (dimensionalFilters) {
      for (var idx in dimensionalFilters) {
        filter.push([dimensionalFilters[idx]])
      }
    }
    filter.push([
      {
        filterKey: 'start_date',
        filterValue: dateRange ? zonedToUTC(dateRange.startDate) : new Date(),
        //filterValue: '2019-09-10',
        condition: 'include',
        filterType: 'gte'
      }
    ])
    filter.push([
      {
        filterKey: 'start_date',
        filterValue: dateRange
          ? zonedToUTCAdd1Day(dateRange.endDate)
          : new Date(),
        //filterValue: '2019-09-10',
        condition: 'include',
        filterType: 'lt'
      }
    ])
    if (!isEmpty(selectedFilter)) {
      const groupedSearch = groupBy(selectedFilter, ele => ele.key)
      const data = Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              res.push(obj.value)
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
      filter = [...filter, ...data]
    }
    if (selectedKey === 'active') {
      filter.push([
        {
          filterKey: 'is_active',
          filterValue: true,
          condition: 'include',
          filterType: 'eq'
        }
      ])
    }
    let query = {
      dataSource: dataSource[graphDataMapping['dataSource'].value],
      ...(!isEmpty(breakDownDimension)
        ? {
            breakDownDimension,
            ...{
              ...(metricCustom.key === 'value_count'
                ? {}
                : { metrics: [metricCustom] })
            }
          }
        : isEmpty(benchmarkDimension)
        ? { metrics: [metricCustom] }
        : {}),
      ...(hideDimension
        ? {}
        : {
            dimension: {
              label: dimension[graphDataMapping['dimension'].value]
            }
          }),
      //   ...(breakDownDimension && { breakDownDimension }),
      ...(benchmarkDimension && { benchmarkDimension }),
      ...(dimensionalAggs ? { dimensionalAggs: dimensionalAggs } : {}),
      sort,
      filter
    }

    try {
      const token = cancelToken().source().token
      const { data } = await post(
        `/api/v1/es/kpi_${isReport ? 'results' : 'aggs'}`,
        query,
        token
      )
      setLastUpdate(currenthhmmA())
      return isReport ? data.results : data.aggs
    } catch (ex:any) {
      setLastUpdate(currenthhmmA())
      return []
    }
  }

  return (
    <Flex column>
      <Header
        lastUpdate={lastUpdate}
        match={match}
        location={location}
        history={history}
        routesConfig={routesConfig}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        toggle={toggleContent}
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        rightTopCorner={
          <DatePicker
            onDateRangeSelected={(dateRange: IDateRange<string | Date>) => {
              localStorage.setItem('date', JSON.stringify(dateRange))
              setDateRange(dateRange as IDateRange<Date>)
            }}
            formatter={yyyyMmDd}
            value={dateRange}
            name="dateFilter"
            variant="fareyeV2"
          />
        }
      >
        <Flex width={1}>
          <SearchFilterWrapper
            apiFav={apiFav}
            pageId={pageId}
            apiFilter={apiFilter}
            search={selectedFilter}
            setSearch={setSelectedFilter}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </Flex>
      </Header>
      <RenderControl
        data={graphRenderListing}
        isSuccess={!loading && !isEmpty(graphRenderListing)}
        isLoading={loading}
        variant={'l'}
        message={
          !isEmpty(graphRenderListing)
            ? ''
            : 'Something went wrong please try after sometime ......'
        }
      >
        <HorizontalScrollGrid>
          {scoreCardRenderingList.map((ele, index) => {
            return <ScoreCard key={`scorecard-${index}`} {...ele}></ScoreCard>
          })}
        </HorizontalScrollGrid>
        <GraphGrid>
          {graphRenderListing.map((ele, index) => {
            return (
              <GraphRenderer
                key={`kpi${index}`}
                {...ele}
                style={{
                  ...ele.style,
                  wrapper: {
                    ...(ele.style && ele.style.wrapper)
                  }
                }}
                FullScreenComponent={
                  <FullScreenComponent
                    dashboardConfig={dashboardConfig}
                    config={ele}
                    location={location}
                    history={history}
                    match={match}
                  />
                }
              />
            )
          })}
        </GraphGrid>
      </RenderControl>
    </Flex>
  )
}
export default ParcelDashboard
